import React from 'react';
import { Table } from 'react-bootstrap';
import { TeamStanding } from '../api/tournament/standingsService';
import { getImageUrl } from '../utils/imageUrl';

interface TeamStandingsTableProps {
  standings: TeamStanding[];
  search?: string;
  viewMode: boolean;
  onEditTeam?: (standing: TeamStanding) => void;
  onRemoveTeam?: (standing: TeamStanding) => void;
}

const TeamStandingsTable: React.FC<TeamStandingsTableProps> = ({
  standings,
  search,
  viewMode,
  onEditTeam,
  onRemoveTeam
}) => {
  // Debug logging
  console.log('TeamStandingsTable - standings:', standings);
  console.log('TeamStandingsTable - search:', search);
  console.log('TeamStandingsTable - standings length:', standings.length);
  
  // Filter out standings with null teams and apply search filter
  const filteredStandings = standings.filter(standing => {
    // First, filter out standings where team is null or undefined
    if (!standing || !standing.team) {
      return false;
    }
    
    if (!search || search.trim() === '') {
      return true; // Show all teams if no search term
    }
    
    const searchTerm = search.toLowerCase().trim();
    const teamName = standing.team.name?.toLowerCase() || '';
    const shortName = standing.team.shortName?.toLowerCase() || '';
    
    return teamName.includes(searchTerm) || shortName.includes(searchTerm);
  });
  
  console.log('TeamStandingsTable - filtered standings:', filteredStandings);
  console.log('TeamStandingsTable - filtered length:', filteredStandings.length);

  return (
    <div className="manage-tournaments-table-container">
      <Table className="mb-0 manage-tournaments-table standings-table">
        <thead>
          <tr>
            <th>Logo</th>
            <th>Team</th>
            <th>Matches</th>
            <th>Wins</th>
            <th>Draws</th>
            <th>Losses</th>
            <th>GF</th>
            <th>GA</th>
            <th>Points</th>
            <th>Qualified</th>
            {/* Actions column hidden as per user request */}
            {/* {!viewMode && <th>Actions</th>} */}
          </tr>
        </thead>
        <tbody>
          {filteredStandings.length === 0 ? (
            <tr>
              <td colSpan={10} className="text-center py-5">
                <div className="text-center">
                  <div className="mb-3">
                    <i className="ri-trophy-line" style={{ fontSize: '64px', opacity: 0.2, color: '#6c757d' }}></i>
                  </div>
                  <h5 className="text-muted mb-2">No Teams in Standings</h5>
                  <p className="text-muted mb-3">
                    {search ? 
                      'No teams match your search criteria' : 
                      'No teams have been added to the standings yet'
                    }
                  </p>
                  {search ? (
                    <small className="text-muted">
                      <i className="ri-search-line me-1"></i>
                      Try adjusting your search terms
                    </small>
                  ) : (
                    <small className="text-muted">
                      <i className="ri-add-circle-line me-1"></i>
                      Click "Edit Teams" to get started
                    </small>
                  )}
                </div>
              </td>
            </tr>
          ) : (
            filteredStandings.map((standing) => {
              // Skip rendering if team is null or undefined
              if (!standing || !standing.team) {
                return null;
              }
              
              return (
              <tr key={standing.team._id}>
                <td>
                  {standing.team.logo ? (
                    <img
                      src={getImageUrl(standing.team.logo)}
                      alt={standing.team.name || 'Team logo'}
                      style={{
                        width: '32px',
                        height: '32px',
                        objectFit: 'cover',
                        borderRadius: '50%'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.setAttribute('style', 'display: flex');
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '50%',
                      display: standing.team.logo ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#6c757d'
                    }}
                  >
                    {standing.team.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center">
                    <div>
                      <div className="fw-semibold text-dark">{standing.team.name || 'Unknown Team'}</div>
                      <small className="text-muted">{standing.team.shortName || ''}</small>
                    </div>
                  </div>
                </td>
                <td>{standing.played}</td>
                <td>
                  <span className="text-success fw-bold">{standing.won}</span>
                </td>
                <td>
                  <span className="text-warning fw-bold">{standing.drawn}</span>
                </td>
                <td>
                  <span className="text-danger fw-bold">{standing.lost}</span>
                </td>
                <td>{standing.goalsFor}</td>
                <td>{standing.goalsAgainst}</td>
                <td>
                  <span className="text-primary fw-bold">{standing.points}</span>
                </td>
                <td>
                  {standing.isQualified !== undefined ? (
                    <span className={`fw-bold ${standing.isQualified ? 'text-success' : 'text-muted'}`}>
                      {standing.isQualified ? "Yes" : "No"}
                    </span>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                {/* Action buttons hidden as per user request */}
                {/* {!viewMode && (
                  <td>
                    <div className="tournament-actions">
                      {onEditTeam && (
                        <button
                          className="action-btn btn-edit"
                          onClick={() => onEditTeam(standing)}
                          title="Edit Team Standings"
                          type="button"
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                      )}
                      {onRemoveTeam && (
                        <button
                          className="action-btn btn-delete"
                          onClick={() => onRemoveTeam(standing)}
                          title="Remove Team"
                          type="button"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </div>
                  </td>
                )} */}
              </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default TeamStandingsTable;
